import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useDescope, useSession, useUser } from '@descope/react-sdk';
import apiService from './api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const { isAuthenticated, isSessionLoading } = useSession();
  const { user: descopeUser, isUserLoading } = useUser();
  const sdk = useDescope();
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tokensReady, setTokensReady] = useState(false);
  
  // Ref for refresh timer
  const refreshTimerRef = useRef(null);

  // Descope project ID
  const descopeProjectId = process.env.REACT_APP_DESCOPE_PROJECT_ID;

  // Helper to decode JWT and get expiration
  const getTokenExpiration = (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000; // Convert to milliseconds
    } catch (error) {
      console.error('[Auth] Error decoding token:', error);
      return null;
    }
  };

  // Schedule proactive token refresh
  const scheduleTokenRefresh = (token) => {
    // Clear existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    const expiration = getTokenExpiration(token);
    if (!expiration) return;

    // Refresh 2 minutes before expiration
    const now = Date.now();
    const timeUntilExpiry = expiration - now;
    const refreshTime = timeUntilExpiry - (2 * 60 * 1000); // 2 min before expiry

    if (refreshTime > 0) {
      console.log(`[Auth] ⏰ Token expires in ${Math.round(timeUntilExpiry / 1000)}s, will refresh in ${Math.round(refreshTime / 1000)}s`);
      
      refreshTimerRef.current = setTimeout(async () => {
        console.log('[Auth] 🔄 Proactive token refresh triggered');
        
        try {
          // Get new token from Descope SDK
          const newToken = sdk.getSessionToken();
          
          if (newToken && newToken !== token) {
            console.log('[Auth] ✅ Got new token from Descope SDK');
            localStorage.setItem('authToken', newToken);
            
            // Schedule next refresh
            scheduleTokenRefresh(newToken);
          } else {
            console.log('[Auth] ⚠️ Token unchanged, will retry in 30s');
            // Token didn't refresh, try again in 30 seconds
            refreshTimerRef.current = setTimeout(() => {
              scheduleTokenRefresh(sdk.getSessionToken());
            }, 30000);
          }
        } catch (error) {
          console.error('[Auth] ❌ Token refresh error:', error);
        }
      }, refreshTime);
    } else if (timeUntilExpiry > 0) {
      // Token expiring very soon, refresh immediately
      console.log('[Auth] ⚠️ Token expiring in <2min, refreshing now');
      setTimeout(() => {
        const newToken = sdk.getSessionToken();
        if (newToken) {
          localStorage.setItem('authToken', newToken);
          scheduleTokenRefresh(newToken);
        }
      }, 1000);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  // Step 1: Wait for Descope SDK to load, then immediately store tokens
  useEffect(() => {
    console.log('[Auth] SDK Loading Status:', { isSessionLoading, isUserLoading, isAuthenticated });

    if (isSessionLoading || isUserLoading) {
      console.log('[Auth] Waiting for Descope SDK to load...');
      return;
    }

    // SDK is loaded - immediately store tokens if authenticated
    if (isAuthenticated) {
      const sessionToken = sdk.getSessionToken();
      const refreshToken = sdk.getRefreshToken();

      console.log('[Auth] Storing tokens:', {
        hasSessionToken: !!sessionToken,
        hasRefreshToken: !!refreshToken
      });

      if (sessionToken) {
        localStorage.setItem('authToken', sessionToken);
        if (refreshToken) {
          localStorage.setItem('refreshToken', refreshToken);
        }
        console.log('[Auth] ✅ Tokens stored in localStorage');
        
        // Schedule proactive refresh
        scheduleTokenRefresh(sessionToken);
      }
    } else {
      console.log('[Auth] Not authenticated, clearing tokens');
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      
      // Clear refresh timer
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    }

    // Mark tokens as ready
    setTokensReady(true);
    console.log('[Auth] ✅ Tokens ready, components can now mount');

  }, [isSessionLoading, isUserLoading, isAuthenticated, sdk]);

  // Step 2: After tokens are ready, validate with backend
  useEffect(() => {
    if (!tokensReady) {
      console.log('[Auth] Waiting for tokens to be ready...');
      return;
    }

    const syncUser = async () => {
      if (isAuthenticated && descopeUser) {
        try {
          const sessionToken = sdk.getSessionToken();
          
          if (sessionToken) {
            console.log('[Auth] Validating token with backend...');
            
            const response = await apiService.post('/auth/validate-token', {}, {
              headers: { Authorization: `Bearer ${sessionToken}` }
            });

            if (response.data.valid) {
              console.log('[Auth] ✅ Token validated, user:', response.data.user.email);
              setUser(response.data.user);
            }
          }
        } catch (error) {
          console.error('[Auth] Validation error:', error);
          // Don't logout - SDK handles session
        }
      } else {
        console.log('[Auth] Not authenticated, clearing user');
        setUser(null);
      }
      
      setLoading(false);
      console.log('[Auth] ✅ Auth initialization complete');
    };

    syncUser();
  }, [tokensReady, isAuthenticated, descopeUser, sdk]);

  // Handle login success
  const handleDescopeSuccess = async (sessionToken, refreshToken) => {
    try {
      console.log('[Auth] Login success, storing tokens');
      
      // Store tokens immediately
      localStorage.setItem('authToken', sessionToken);
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }

      // Schedule proactive refresh
      scheduleTokenRefresh(sessionToken);

      // Validate with backend
      const response = await apiService.post('/auth/validate-token', {}, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });

      if (response.data.valid) {
        setUser(response.data.user);
        console.log('[Auth] ✅ Login complete');
        return true;
      }
      return false;
    } catch (error) {
      console.error('[Auth] Login validation failed:', error);
      return false;
    }
  };

  // Logout
  const logout = () => {
    console.log('[Auth] Logging out');
    
    // Clear refresh timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    
    // Logout from Descope SDK
    sdk.logout();
    
    // Clear local state
    setUser(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    delete apiService.api.defaults.headers.common['Authorization'];
  };

  // Role and permission helpers
  const hasRole = (role) => {
    return user?.role === role;
  };

  const hasPermission = (permission) => {
    return user?.permissions?.includes(permission);
  };

  const canAccessFacility = (facilityId) => {
    if (user?.role === 'executive_director') return true;
    if (user?.role === 'plant_manager') {
      return user?.assigned_facilities?.includes(facilityId);
    }
    return true;
  };

  const value = {
    user,
    token: sdk.getSessionToken(),
    refreshToken: sdk.getRefreshToken(),
    loading,
    isAuthenticated,
    login: handleDescopeSuccess,
    logout,
    hasRole,
    hasPermission,
    canAccessFacility,
    descopeProjectId,
  };

  // CRITICAL: Don't render children until tokens are ready
  if (!tokensReady) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: '#f8fafc'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #667eea',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <p style={{ color: '#666', fontSize: '14px' }}>Initializing...</p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};